import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'antd';
import { CheckOutlined, DownOutlined, EditOutlined, FolderOutlined, PlusOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useProjects } from '../contexts/ProjectContext';
import { Project } from '../api/projects';
import { getInvoices, Invoice } from '../firebase/firestore';
import { useI18n } from '../i18n/I18nContext';
import { app as enApp } from '../i18n/locales/app/en';
import type { AppProjectsMessages } from '../i18n/types';
import './ProjectSwitcher.css';

const apiMessage = (err: any, fallback: string) => {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join(', ');
  if (typeof message === 'string' && message.trim()) return message;
  return fallback;
};

const ProjectFormModal: React.FC<{
  open: boolean;
  project: Project | null;
  labels: AppProjectsMessages;
  onClose: () => void;
}> = ({ open, project, labels, onClose }) => {
  const { firebaseUser } = useAuth();
  const { t } = useI18n();
  const { createProject, updateProject, deleteProject } = useProjects();
  const [name, setName] = useState('');
  const [query, setQuery] = useState('');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(project?.name ?? '');
    setQuery('');
    setSelected(new Set(project?.invoiceIds ?? []));
    setError(null);
    setConfirmDelete(false);
    if (!firebaseUser) return;
    let cancelled = false;
    setLoading(true);
    getInvoices(firebaseUser.uid, undefined, 500)
      .then((rows) => {
        if (!cancelled) setInvoices(rows);
      })
      .catch(() => {
        if (!cancelled) setError(labels.failed);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, project, firebaseUser, labels.failed]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return invoices;
    return invoices.filter((invoice) => {
      const hay = `${invoice.title || ''} ${invoice.description || ''} ${invoice.id}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [invoices, query]);

  const statusLabel = (status: Invoice['status']) => {
    switch (status) {
      case 'PAID': return t.app.invoices.statusPaid;
      case 'SENT': return t.app.invoices.statusSent;
      case 'CREATED': return t.app.invoices.statusCreated;
      case 'EXPIRED':
      case 'CANCELED': return t.app.invoices.statusExpired;
      default: return status;
    }
  };

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError(labels.nameRequired);
      return;
    }
    setSaving(true);
    setError(null);
    const input = { name: trimmed, invoiceIds: Array.from(selected) };
    try {
      if (project) await updateProject(project.id, input);
      else await createProject(input);
      onClose();
    } catch (err) {
      setError(apiMessage(err, labels.failed));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!project) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await deleteProject(project.id);
      onClose();
    } catch (err) {
      setError(apiMessage(err, labels.failed));
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={520}
      className="project-modal"
      title={project ? labels.editTitle : labels.createTitle}
    >
      <form className="project-form" onSubmit={submit}>
        <label className="project-field">
          <span>{labels.name}</span>
          <input
            className="project-input"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={labels.namePlaceholder}
            maxLength={80}
            autoFocus
          />
        </label>

        <div className="project-field">
          <span>{labels.invoices}</span>
          <p className="project-hint">{labels.invoicesHint}</p>
          <input
            className="project-input"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={labels.searchInvoices}
          />
          <div className="project-invoice-list" role="group" aria-label={labels.invoices}>
            {loading && <p className="project-invoice-empty">{labels.loadingInvoices}</p>}
            {!loading && invoices.length === 0 && <p className="project-invoice-empty">{labels.noInvoices}</p>}
            {!loading && invoices.length > 0 && visible.length === 0 && (
              <p className="project-invoice-empty">{labels.noMatch}</p>
            )}
            {!loading && visible.map((invoice) => {
              const checked = selected.has(invoice.id);
              const title = invoice.title || invoice.description || invoice.id.slice(0, 8);
              return (
                <label key={invoice.id} className={`project-invoice ${checked ? 'is-checked' : ''}`}>
                  <input type="checkbox" checked={checked} onChange={() => toggle(invoice.id)} />
                  <span className="project-invoice-text">
                    <span className="project-invoice-title">{title}</span>
                    <span className="project-invoice-meta">
                      {invoice.starsAmount.toLocaleString()} ★
                      {invoice.status ? ` · ${statusLabel(invoice.status)}` : ''}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
          <p className="project-selected-count">{labels.selectedCount.replace('{count}', String(selected.size))}</p>
        </div>

        {error && <p className="project-form-error">{error}</p>}

        <div className="project-form-actions">
          {project ? (
            <button type="button" className="project-btn project-btn-danger" onClick={remove} disabled={saving}>
              {confirmDelete ? labels.confirmDelete : labels.delete}
            </button>
          ) : <span />}
          <div className="project-form-actions-right">
            <button type="button" className="project-btn" onClick={onClose} disabled={saving}>
              {labels.cancel}
            </button>
            <button type="submit" className="project-btn project-btn-primary" disabled={saving}>
              {saving ? labels.saving : project ? labels.save : labels.create}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

const ProjectSwitcher: React.FC = () => {
  const { t } = useI18n();
  const labels = (t.app.projects || enApp.projects) as AppProjectsMessages;
  const { projects, selectedProject, selectProject } = useProjects();
  const [open, setOpen] = useState(false);
  const [modalProject, setModalProject] = useState<Project | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openCreate = () => {
    setOpen(false);
    setModalProject(null);
    setModalOpen(true);
  };

  const openEdit = (project: Project) => {
    setOpen(false);
    setModalProject(project);
    setModalOpen(true);
  };

  return (
    <div className="project-switcher" ref={rootRef}>
      <button
        type="button"
        className={`project-switcher-trigger ${open ? 'is-open' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <FolderOutlined />
        <span className="project-switcher-label">{selectedProject?.name || labels.all}</span>
        <DownOutlined className="project-switcher-chevron" />
      </button>

      {open && (
        <div className="project-switcher-menu" role="listbox">
          <button
            type="button"
            role="option"
            aria-selected={!selectedProject}
            className={`project-switcher-item ${!selectedProject ? 'is-active' : ''}`}
            onClick={() => {
              selectProject(null);
              setOpen(false);
            }}
          >
            <CheckOutlined className="project-switcher-check" />
            <span className="project-switcher-item-name">{labels.all}</span>
          </button>

          <div className="project-switcher-list">
            {projects.length === 0 && <p className="project-switcher-empty">{labels.emptyList}</p>}
            {projects.map((project) => {
              const active = selectedProject?.id === project.id;
              return (
                <div key={project.id} className={`project-switcher-row ${active ? 'is-active' : ''}`}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    className="project-switcher-item"
                    onClick={() => {
                      selectProject(project.id);
                      setOpen(false);
                    }}
                  >
                    <CheckOutlined className="project-switcher-check" />
                    <span className="project-switcher-item-name">{project.name}</span>
                    <span className="project-switcher-count">{project.invoiceIds.length}</span>
                  </button>
                  <button
                    type="button"
                    className="project-switcher-edit"
                    title={labels.editTitle}
                    aria-label={labels.editTitle}
                    onClick={() => openEdit(project)}
                  >
                    <EditOutlined />
                  </button>
                </div>
              );
            })}
          </div>

          <button type="button" className="project-switcher-new" onClick={openCreate}>
            <PlusOutlined />
            {labels.newProject}
          </button>
        </div>
      )}

      <ProjectFormModal
        open={modalOpen}
        project={modalProject}
        labels={labels}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
};

export default ProjectSwitcher;
